import { Colors, FOCUS_RING, Icon } from '@amelio/ui-library';

import type { EvaluationStep, StepDefinition } from '../../Evaluation/types';

import { useAmelioTranslations } from '@/hooks';

interface StepNavigationProps {
    // The step whose page is currently open (the selected tab). Drives both the active highlight
    // and the progress bar/completed checks, so the progress follows the step selected in the nav.
    currentStep: EvaluationStep;
    employeeFirstName?: string;
    onStepClick: (step: StepDefinition) => void;
    dataTestId?: string;
}

const STEPS: StepDefinition[] = [
    { id: 1, labelKey: 'managerAssessmentEvaluation.steps.evaluation', route: 'evaluation' },
    { id: 2, labelKey: 'managerAssessmentEvaluation.steps.results', route: 'results' },
    {
        id: 3,
        labelKey: 'managerAssessmentEvaluation.steps.prepareMeeting',
        route: 'prepare-meeting',
    },
    { id: 4, labelKey: 'managerAssessmentEvaluation.steps.meet', route: 'meet-employee' },
];

const getStepStyles = (isCompleted: boolean, isActive: boolean): string => {
    // The step you're viewing wins visually, even if it's also been completed. The hover states read
    // from the button's `group`, not from the badge itself: the whole tile is the control, so hovering
    // the wording has to light the badge too.
    if (isActive) {
        return 'bg-linear-to-br from-primary to-blue-500 border-primary shadow-primary/30 ring-primary/20 shadow-lg ring-2 group-hover:shadow-xl';
    }
    if (isCompleted) {
        return 'bg-linear-to-br from-emerald-400 to-teal-500 border-emerald-500 shadow-md shadow-emerald-200 group-hover:shadow-lg';
    }
    return 'bg-white border-gray-300 shadow-sm group-hover:shadow-md group-hover:border-gray-400';
};

const getLabelStyles = (isCompleted: boolean, isActive: boolean): string => {
    if (isActive) {
        return 'text-primary';
    }
    if (isCompleted) {
        return 'text-teal-700';
    }
    return 'text-gray-600';
};

/* Returned as a class, not a `Colors` constant: the reached chevron has to read as the same green
   as the completed badge it follows, and that badge is drawn with `emerald-500`. `Colors.SUCCESS`
   is a far brighter green and made the separator louder than the step it belongs to. */
const getChevronColorClass = (isCompleted: boolean): string =>
    isCompleted ? 'text-emerald-500' : 'text-base-200';

/* The badge is the bar's vertical axis, declared once: the chevrons are centred in a box of the
   same height, so they stay on the badges' line whatever the labels below them do. Change this one
   value and the whole row follows. */
const STEP_AXIS_HEIGHT = 'h-7 max-md:h-6';

export const StepNavigation = ({
    currentStep,
    employeeFirstName,
    onStepClick,
    dataTestId = 'step-navigation',
}: StepNavigationProps) => {
    const { t } = useAmelioTranslations();

    /* The bar follows the selected step so the progress moves with the nav selection. One column
       per step, and the fill stops at the centre of the selected step's column, which lands it
       under that step's badge. Two consequences are intended, not defects to round off: the track
       runs half a column past the first and last badges, and on the last step a half-column stays
       unfilled — the bar says which step you are on, not how much of the journey is behind you. */
    const progressPercentage = ((currentStep - 0.5) / STEPS.length) * 100;

    return (
        <nav
            aria-label={t('managerAssessmentEvaluation.steps.navLabel')}
            className={'flex w-full flex-col gap-[8px] max-md:gap-[4px]'}
            data-testid={dataTestId}
        >
            {/* `list-none` and `p-0` are explicit rather than left to the reset: the module renders
                inside the host's shadow root, where a missing preflight would hand the list back
                its markers and its 40px indent. */}
            <ol className={'flex list-none items-center gap-[4px] p-0 max-md:gap-[2px]'}>
                {STEPS.map((step, index) => {
                    const isActive = step.id === currentStep;
                    const isCompleted = step.id < currentStep;
                    const isLastStep = index === STEPS.length - 1;

                    const translationKey = step.labelKey;
                    const stepLabel =
                        step.id === 4 && employeeFirstName
                            ? `${t(translationKey)} ${employeeFirstName}`
                            : t(translationKey);

                    return (
                        <li
                            key={step.id}
                            className={'flex min-w-0 flex-1 items-start gap-[4px] max-md:gap-[2px]'}
                        >
                            {/* The button holds the badge and the label, so the whole step reads and
                                clicks as one control: clicking the wording used to do nothing. It
                                hugs its content rather than filling the column, so no click lands
                                far from what looks clickable. */}
                            <div className={'flex min-w-0 flex-1 flex-col items-center'}>
                                <button
                                    type={'button'}
                                    onClick={() => onStepClick(step)}
                                    aria-current={isActive ? 'step' : undefined}
                                    aria-label={t(
                                        'managerAssessmentEvaluation.steps.stepAriaLabel',
                                        { rank: step.id, total: STEPS.length, label: stepLabel },
                                    )}
                                    className={`group flex w-fit max-w-full min-w-0 cursor-pointer flex-col items-center gap-[4px] bg-transparent max-md:gap-[2px] ${FOCUS_RING}`}
                                    data-testid={`step-button-${step.id}`}
                                >
                                    <span
                                        aria-hidden={'true'}
                                        className={`${STEP_AXIS_HEIGHT} flex aspect-square shrink-0 items-center justify-center rounded-full border-2 transition-all duration-300 ${getStepStyles(isCompleted, isActive)}`}
                                    >
                                        {isCompleted ? (
                                            <Icon
                                                name={'essential-tick-circle'}
                                                size={'xs'}
                                                color={Colors.WHITE}
                                            />
                                        ) : (
                                            <span
                                                className={`text-xs font-bold transition-colors ${isActive ? 'text-white' : 'text-gray-500'}`}
                                            >
                                                {step.id}
                                            </span>
                                        )}
                                    </span>

                                    {/* The label wraps rather than truncating: a step clipped to
                                        "Prepare the me…" no longer names where it leads, and a
                                        narrow window is where that name matters most. One step,
                                        not two: `text-xs` is the bottom of the module's scale, so
                                        the narrow width already stood here and stays. */}
                                    <span
                                        aria-hidden={'true'}
                                        className={`max-w-full text-center text-xs leading-tight font-semibold transition-colors ${getLabelStyles(isCompleted, isActive)}`}
                                    >
                                        {stepLabel}
                                    </span>
                                </button>
                            </div>

                            {/* Chevron separator, in a box as tall as the badge so it sits on the
                                badges' line and not halfway down a wrapped label. `xs` is the
                                icon step that renders 12px; a numeric size would be written as an
                                inline style, which no class could then override. */}
                            {!isLastStep && (
                                <span
                                    aria-hidden={'true'}
                                    className={`${STEP_AXIS_HEIGHT} -mx-0.5 flex shrink-0 items-center`}
                                >
                                    <Icon
                                        name={'arrow-arrow-right-1'}
                                        size={'xs'}
                                        colorClassName={getChevronColorClass(isCompleted)}
                                        ariaHidden={true}
                                    />
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>

            <div
                className={'h-[4px] w-full overflow-hidden rounded-full bg-gray-200/60'}
                role={'progressbar'}
                aria-label={t('managerAssessmentEvaluation.steps.progressLabel')}
                aria-valuenow={currentStep}
                aria-valuemin={1}
                aria-valuemax={STEPS.length}
                aria-valuetext={t('managerAssessmentEvaluation.steps.progressValue', {
                    rank: currentStep,
                    total: STEPS.length,
                })}
                data-testid={'step-progress-bar'}
            >
                <div
                    className={
                        'from-primary shadow-primary/30 h-full rounded-full bg-linear-to-r via-blue-500 to-purple-500 shadow-lg'
                    }
                    style={{
                        width: `${progressPercentage}%`,
                        transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                />
            </div>
        </nav>
    );
};
